import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, Globe, AlertTriangle, CheckCircle2 } from "lucide-react";
import DomainHealthCard from "./DomainHealthCard";
import {
  TenantContactTab,
  TenantIntegrationsTab,
  TenantOverviewTab,
  TenantPlanTab,
  TenantUsageTab,
  TenantUsersTab,
} from "./TenantDetailTabs";
import SuperAdminTenantBillingTab from "./SuperAdminTenantBillingTab"; // iter334cj
import PaymentOperations from "../admin/PaymentOperations"; // iter391 — super-admin only

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const _emptyBranding = {
  business_name: "",
  business_tagline: "",
  business_phone: "",
  whatsapp_number: "",
  support_email: "",
  address: "",
  website: "",
  primary_color: "#1e293b",
  accent_color: "#0ea5e9",
  hero_copy: "",
  social_links: { facebook: "", instagram: "", twitter: "" },
};

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-xs uppercase tracking-wider text-slate-500">
      {label}
    </span>
    {children}
  </label>
);

export default function SuperAdminTenantDetail() {
  const { tenantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDomain, setSavingDomain] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [branding, setBranding] = useState(_emptyBranding);
  const [customDomain, setCustomDomain] = useState("");
  const [canonicalHostname, setCanonicalHostname] = useState("");
  // iter222 — Tenant-scoped opt-out of the defensive `www.<custom_domain>`
  // reachability probe. Prevents perpetual "www variant unreachable"
  // noise when the tenant only serves the apex.
  const [skipWwwCheck, setSkipWwwCheck] = useState(false);
  // iter312 — host-reachability probes for the canonical-hostname
  // warning UI (apex / www / subdomain).
  const [hostProbes, setHostProbes] = useState({});
  const [probing, setProbing] = useState(false);
  // iter309-redesign PR 2 — tabbed detail page. Branding/domain content
  // is preserved verbatim from the pre-PR-2 design; other tabs are
  // scaffolding for the data endpoints landing in PR 3+.
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined") {
      const qs = new URLSearchParams(window.location.search);
      const t = qs.get("tab");
      if (t) return t;
    }
    return "overview";
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/super-admin/tenants/${tenantId}`,
        { headers: authHeader() }
      );
      setTenant(data.tenant);
      setCustomDomain(data.tenant?.custom_domain || "");
      setCanonicalHostname(data.tenant?.canonical_hostname || "");
      setSkipWwwCheck(!!data.tenant?.skip_www_check);
      // Merge tenant.branding overrides + business_settings + defaults
      // so the editor shows the same values the public branding API
      // returns.
      const tBrand = data.tenant?.branding || {};
      const biz = data.business_settings || {};
      setBranding({
        business_name:
          tBrand.business_name || biz.business_name || data.tenant?.name || "",
        business_tagline:
          tBrand.business_tagline || biz.business_tagline || "",
        business_phone: tBrand.business_phone || biz.business_phone || "",
        whatsapp_number: tBrand.whatsapp_number || biz.whatsapp_number || "",
        support_email:
          tBrand.support_email || biz.delivery_email || biz.support_email || "",
        address: tBrand.address || biz.address || "",
        website: tBrand.website || biz.website || "",
        primary_color:
          tBrand.primary_color || biz.primary_color || "#1e293b",
        accent_color:
          tBrand.accent_color || biz.accent_color || "#0ea5e9",
        hero_copy: tBrand.hero_copy || biz.hero_copy || "",
        social_links:
          tBrand.social_links ||
          biz.social_links || { facebook: "", instagram: "", twitter: "" },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tenant");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBranding = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/super-admin/tenants/${tenantId}/branding`,
        branding,
        { headers: authHeader() }
      );
      toast.success("Branding saved — public site updates within seconds");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      await axios.patch(
        `${API}/super-admin/tenants/${tenantId}/domain`,
        {
          custom_domain: customDomain || null,
          canonical_hostname: canonicalHostname || null,
          skip_www_check: skipWwwCheck,
        },
        { headers: authHeader() }
      );
      toast.success(
        customDomain
          ? `Domain ${customDomain} attached`
          : "Custom domain detached"
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update domain");
    } finally {
      setSavingDomain(false);
    }
  };

  // iter312 — Probe each candidate hostname (apex, www, subdomain) to
  // verify Cloudflare-for-SaaS is actually routing it to the backend.
  // We do this by hitting `/api/public/host-debug` from the SUPER ADMIN
  // side — any unreachable host means "404 / connection closed" and
  // production users on that variant will see the broken-page cascade.
  const probeHosts = async () => {
    if (!tenant) return;
    setProbing(true);
    const candidates = [
      tenant.custom_domain,
      // iter222 — honour tenant.skip_www_check so we don't perpetually
      // flag NXDOMAIN on a variant the tenant intentionally doesn't serve.
      tenant.custom_domain && !tenant.skip_www_check
        ? `www.${tenant.custom_domain}`
        : null,
      tenant.subdomain_host,
    ].filter(Boolean);
    const results = {};
    await Promise.all(
      candidates.map(async (host) => {
        try {
          const r = await fetch(`https://${host}/api/public/host-debug`, {
            method: "GET",
            cache: "no-store",
          });
          const body = await r.json().catch(() => ({}));
          results[host] = {
            ok: r.ok,
            status: r.status,
            resolved: body?.effective_host_resolved || null,
          };
        } catch (e) {
          results[host] = { ok: false, error: e?.message || "unreachable" };
        }
      })
    );
    setHostProbes(results);
    setProbing(false);
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="super-admin-tenant-detail-page">
      <Link
        to="/super-admin/tenants"
        className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
        data-testid="back-to-tenants"
      >
        <ArrowLeft className="w-3 h-3" /> Back to tenants
      </Link>
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span
            className="w-3 h-3 rounded-full ring-2 ring-white shadow"
            style={{ backgroundColor: branding.primary_color }}
          />
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {tenant?.name}
            </h1>
            <p className="text-xs text-slate-500 font-mono">
              {tenant?.id} · slug: {tenant?.slug}
            </p>
          </div>
        </div>
        {activeTab === "branding" && (
          <button
            onClick={saveBranding}
            disabled={saving}
            data-testid="save-branding-btn"
            className="bg-slate-900 text-white rounded px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-slate-800 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save branding
          </button>
        )}
      </header>

      {/* iter309-redesign PR 2 — Tab bar. Tab data endpoints land in PR 3+. */}
      <nav
        className="flex flex-wrap gap-1 border-b border-slate-200"
        data-testid="tenant-detail-tabs"
      >
        {[
          { k: "overview",     label: "Overview" },
          { k: "contact",      label: "Contact" },
          { k: "plan",         label: "Plan & Features" },
          { k: "saas_billing", label: "SaaS Billing" },
          { k: "domains",      label: "Domains" },
          { k: "branding",     label: "Branding" },
          { k: "users",        label: "Users" },
          { k: "usage",        label: "Usage & Limits" },
          { k: "integrations", label: "Integrations" },
          { k: "payment_ops",  label: "Payment Operations" },
          { k: "activity",     label: "Activity & Audit" },
        ].map(({ k, label }) => (
          <button
            key={k}
            type="button"
            data-testid={`tenant-tab-${k}`}
            onClick={() => {
              setActiveTab(k);
              const url = new URL(window.location.href);
              url.searchParams.set("tab", k);
              window.history.replaceState({}, "", url.toString());
            }}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              activeTab === k
                ? "border-slate-900 text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {activeTab === "overview" && (
        <TenantOverviewTab tenantId={tenantId} />
      )}
      {activeTab === "contact" && <TenantContactTab tenantId={tenantId} />}
      {activeTab === "plan"    && <TenantPlanTab tenantId={tenantId} />}
      {activeTab === "saas_billing" && <SuperAdminTenantBillingTab tenantId={tenantId} />}
      {activeTab === "users"   && <TenantUsersTab tenantId={tenantId} />}
      {activeTab === "usage"   && <TenantUsageTab tenantId={tenantId} />}
      {activeTab === "integrations" && <TenantIntegrationsTab tenantId={tenantId} />}
      {activeTab === "payment_ops" && <PaymentOperations tenantId={tenantId} />}
      {activeTab === "activity"     && <TabPlaceholder pr="PR 3" title="Activity & Audit" />}

      {/* Branding tab: live preview + full branding editor.
          Preserves the pre-PR-2 behavior verbatim. */}
      {activeTab === "branding" && (<>

      {/* ===== LIVE PREVIEW ===== */}
      <section
        className="rounded-lg border border-slate-200 overflow-hidden shadow-sm"
        data-testid="branding-preview"
      >
        <div className="px-3 py-2 bg-slate-50 border-b text-xs text-slate-500 uppercase tracking-wider">
          Live preview
        </div>
        <div
          className="p-8"
          style={{
            background: `linear-gradient(135deg, ${branding.primary_color}, ${branding.accent_color})`,
          }}
        >
          <div className="bg-white/95 backdrop-blur rounded-lg p-6 max-w-xl">
            <h2
              className="text-2xl font-bold mb-1"
              style={{ color: branding.primary_color }}
            >
              {branding.business_name || tenant?.name || "Cricket Academy"}
            </h2>
            {branding.business_tagline && (
              <p className="text-sm text-slate-500 mb-3">
                {branding.business_tagline}
              </p>
            )}
            {branding.hero_copy && (
              <p className="text-base text-slate-700 mt-3">
                {branding.hero_copy}
              </p>
            )}
            <div className="mt-4 text-xs text-slate-500 space-y-0.5">
              {branding.address && <div>📍 {branding.address}</div>}
              {branding.business_phone && <div>📞 {branding.business_phone}</div>}
              {branding.support_email && <div>✉️ {branding.support_email}</div>}
            </div>
          </div>
        </div>
      </section>

      {/* ===== BRANDING EDITOR ===== */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold">Identity</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Business name" testid="field-business-name">
            <input
              value={branding.business_name}
              onChange={(e) =>
                setBranding({ ...branding, business_name: e.target.value })
              }
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
              data-testid="input-business-name"
            />
          </Field>
          <Field label="Tagline" testid="field-tagline">
            <input
              value={branding.business_tagline}
              onChange={(e) =>
                setBranding({ ...branding, business_tagline: e.target.value })
              }
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
              placeholder="Where Champions Are Made"
            />
          </Field>
          <Field label="Hero copy" testid="field-hero-copy">
            <textarea
              value={branding.hero_copy}
              onChange={(e) =>
                setBranding({ ...branding, hero_copy: e.target.value })
              }
              rows={3}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
              placeholder="Train like a champion. Play like a pro."
            />
          </Field>
          <Field label="Address" testid="field-address">
            <input
              value={branding.address}
              onChange={(e) =>
                setBranding({ ...branding, address: e.target.value })
              }
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
            />
          </Field>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold">Colors</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Primary color" testid="field-primary-color">
            <div className="flex items-center gap-3 mt-1">
              <input
                type="color"
                value={branding.primary_color}
                onChange={(e) =>
                  setBranding({ ...branding, primary_color: e.target.value })
                }
                className="w-12 h-10 rounded border border-slate-300 cursor-pointer"
                data-testid="input-primary-color"
              />
              <input
                value={branding.primary_color}
                onChange={(e) =>
                  setBranding({ ...branding, primary_color: e.target.value })
                }
                className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
          </Field>
          <Field label="Accent color" testid="field-accent-color">
            <div className="flex items-center gap-3 mt-1">
              <input
                type="color"
                value={branding.accent_color}
                onChange={(e) =>
                  setBranding({ ...branding, accent_color: e.target.value })
                }
                className="w-12 h-10 rounded border border-slate-300 cursor-pointer"
                data-testid="input-accent-color"
              />
              <input
                value={branding.accent_color}
                onChange={(e) =>
                  setBranding({ ...branding, accent_color: e.target.value })
                }
                className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
          </Field>
        </div>
      </section>

      {/* iter335i — Contact fields (support email / phone / whatsapp /
          website) were removed from the Branding tab: they duplicated the
          dedicated Contact tab. Edit them there — this tab is now identity,
          colors & hero copy only. */}
      </>)}

      {/* Domains tab: custom domain form + host probes + DomainHealthCard.
          Preserves the pre-PR-2 behavior verbatim. */}
      {activeTab === "domains" && (<>
      {/* ===== CUSTOM DOMAIN ===== */}
      <section className="bg-white border border-slate-200 rounded-lg p-6 space-y-3">
        <h3 className="font-semibold flex items-center gap-2">
          <Globe className="w-4 h-4" /> Custom domain
        </h3>
        <p className="text-xs text-slate-500">
          Customer-owned domain that resolves to this tenant. Customer must
          CNAME this hostname at their registrar to the platform host. SSL is
          provisioned by Emergent Support after DNS verifies.
        </p>
        <div className="space-y-3">
          <Field label="Custom domain (apex)" testid="field-custom-domain">
            <input
              value={customDomain}
              onChange={(e) => setCustomDomain(e.target.value)}
              placeholder="acme.example.com"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono mt-1"
              data-testid="input-custom-domain"
            />
          </Field>
          <Field
            label="Canonical hostname (cross-host redirect target)"
            testid="field-canonical-hostname"
          >
            <input
              value={canonicalHostname}
              onChange={(e) => setCanonicalHostname(e.target.value)}
              placeholder="(leave blank to use custom domain)"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm font-mono mt-1"
              data-testid="input-canonical-hostname"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Where <span className="font-mono">&lt;slug&gt;.vyomos.io</span> and sign-ins
              send visitors. Defaults to{" "}
              <span className="font-mono">custom_domain</span>. Use the{" "}
              <span className="font-mono">www.</span> host here if the bare domain has no DNS record.
            </p>
          </Field>

          {/* iter222 — Opt out of www. reachability check */}
          <label
            className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer select-none"
            data-testid="field-skip-www-check"
          >
            <input
              type="checkbox"
              checked={skipWwwCheck}
              onChange={(e) => setSkipWwwCheck(e.target.checked)}
              className="mt-0.5"
              data-testid="checkbox-skip-www-check"
            />
            <span>
              <strong>Skip the www. reachability probe.</strong>
              <span className="block text-slate-500 mt-0.5">
                Enable this if the tenant only serves the apex domain (no
                www. CNAME configured). Otherwise the diagnostic below will
                always flag <span className="font-mono">www.{customDomain || "…"}</span>{" "}
                as unreachable.
              </span>
            </span>
          </label>
          <div className="flex items-center justify-end">
            <button
              onClick={saveDomain}
              disabled={savingDomain}
              data-testid="save-domain-btn"
              className="bg-slate-900 text-white rounded px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-slate-800 disabled:opacity-50"
            >
              {savingDomain ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        </div>

        {/* iter312 — Reachability warning UI */}
        <div className="border-t border-slate-200 pt-4 mt-2 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">Cloudflare-for-SaaS reachability</h4>
            <button
              onClick={probeHosts}
              disabled={probing || !tenant?.custom_domain}
              className="text-xs px-3 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              data-testid="probe-hosts-btn"
            >
              {probing ? (
                <Loader2 className="w-3 h-3 animate-spin inline" />
              ) : (
                "Check now"
              )}
            </button>
          </div>
          <p className="text-[11px] text-slate-500">
            Verifies each candidate hostname is routed to the backend. Add
            missing hosts to the Cloudflare-for-SaaS dashboard or set the
            canonical_hostname above so users are never bounced to an
            unreachable variant.
          </p>
          {Object.keys(hostProbes).length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              Click &quot;Check now&quot; to probe configured hosts.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="host-probe-results">
              {Object.entries(hostProbes).map(([host, result]) => {
                const reachable = result.ok && result.status === 200;
                // iter222 — When the origin returned 403/503 (Emergent
                // ingress rejects an unregistered hostname), show a
                // targeted hint that tells the admin exactly what to
                // do rather than the generic "http 503" line.
                const originNotProvisioned =
                  !reachable &&
                  [403, 502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530].includes(
                    result.status
                  );
                return (
                  <li
                    key={host}
                    className="text-xs bg-slate-50 rounded px-3 py-2"
                    data-testid={`host-probe-${host}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono">{host}</span>
                      {reachable ? (
                        <span className="inline-flex items-center gap-1 text-green-700">
                          <CheckCircle2 className="w-3.5 h-3.5" /> reachable
                          {result.resolved && result.resolved !== host && (
                            <span className="text-slate-400 ml-1">
                              (resolved as {result.resolved})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="w-3.5 h-3.5" />{" "}
                          {result.error || `http ${result.status || "??"}`}
                        </span>
                      )}
                    </div>
                    {originNotProvisioned && (
                      <div
                        className="mt-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                        data-testid={`host-hint-${host}`}
                      >
                        <strong>Emergent hasn&apos;t provisioned this
                        hostname yet.</strong>{" "}
                        DNS + SSL are green but the origin is refusing
                        the Host header. Add{" "}
                        <span className="font-mono">{host}</span> as a
                        custom domain on your Emergent deployment (or
                        contact Emergent Support) so the ingress accepts
                        traffic.
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {/* iter323c — Domain Health widget: DNS / SSL / reachability /
          CORS allow-list membership per candidate hostname, with a
          one-click "Re-add to CORS" button when drift is detected. */}
      <DomainHealthCard tenantId={tenantId} />
      </>)}
    </div>
  );
}

// iter309-redesign PR 2 — placeholder shell shown for tabs whose data
// endpoints land in later PRs. Not exported.
function TabPlaceholder({ pr, title }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg p-8 text-center"
      data-testid={`tenant-tab-placeholder-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className="text-slate-500 text-sm">
        <span className="font-medium text-slate-700">{title}</span> lands in {pr}.
      </p>
      <p className="text-xs text-slate-400 mt-1">
        This tab is scaffolding only. The tenant-detail aggregate endpoint
        and per-tab data queries will populate it in the next iteration.
      </p>
    </div>
  );
}

